import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { syncFolderHasChildrenFromBranchQueryKey } from "@src/components/board/queries/navbar-branch-has-children";
import { graphql } from "@src/gql/gql";
import type { NavbarSection } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const moveRanklistItemMutation = graphql(/* GraphQL */ `
	mutation moveRanklistItem(
		$fromSection: NavbarSection!
		$toSection: NavbarSection!
		$items: [ItemToMoveInput!]!
		$waitForProjections: Boolean
	) {
		navbar {
			moveItems(
				fromSection: $fromSection
				toSection: $toSection
				items: $items
				waitForProjections: $waitForProjections
			) {
				key
				name
				parentKey
				rank
				type
			}
		}
	}
`);

export type MoveItemSpec = {
	itemKey: string;
	parentKey?: string | null;
	sourceParentKey?: string | null;
	beforeKey?: string | null;
	afterKey?: string | null;
	targetIndex?: number;
};

type SingleItemInput = {
	section: NavbarSection;
	fromSection?: NavbarSection;
	toSection?: NavbarSection;
	itemKey: string;
	parentKey?: string | null;
	sourceParentKey?: string | null;
	targetIndex?: number;
	beforeKey?: string | null;
	afterKey?: string | null;
};

type BatchInput = {
	section: NavbarSection;
	fromSection?: NavbarSection;
	toSection?: NavbarSection;
	items: MoveItemSpec[];
};

export type MoveInput = SingleItemInput | BatchInput;

function normalizeInput(input: MoveInput) {
	const section = input.section;
	const fromSection = input.fromSection ?? section;
	const toSection = input.toSection ?? section;

	if ("items" in input) {
		return { section, fromSection, toSection, items: input.items };
	}

	return {
		section,
		fromSection,
		toSection,
		items: [
			{
				itemKey: input.itemKey,
				parentKey: input.parentKey,
				sourceParentKey: input.sourceParentKey,
				beforeKey: input.beforeKey,
				afterKey: input.afterKey,
				targetIndex: input.targetIndex,
			},
		],
	};
}

function toBranchKeyStr(
	section: NavbarSection,
	parentKey: string | null | undefined,
): string {
	return `${section}\0${parentKey ?? "root"}`;
}

type BranchSnapshot = {
	queryKey: readonly string[];
	data: NavbarBranchItem[] | undefined;
};

export function useMoveRanklistItem() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: MoveInput) => {
			const { fromSection, toSection, items } = normalizeInput(input);
			const response = await client.mutate({
				mutation: moveRanklistItemMutation,
				variables: {
					fromSection,
					toSection,
					items: items.map(({ itemKey, parentKey, beforeKey, afterKey }) => ({
						itemKey,
						parentKey: parentKey ?? null,
						beforeKey: beforeKey ?? null,
						afterKey: afterKey ?? null,
					})),
					waitForProjections: true,
				},
			});
			return response.data?.navbar?.moveItems ?? [];
		},

		onMutate: async (input) => {
			const { fromSection, toSection, items } = normalizeInput(input);

			await queryClient.cancelQueries({
				queryKey: navbarBranchQueryKeys.section(fromSection),
			});
			if (toSection !== fromSection) {
				await queryClient.cancelQueries({
					queryKey: navbarBranchQueryKeys.section(toSection),
				});
			}

			const snapshots = new Map<string, BranchSnapshot>();

			const ensureSnapshot = (
				section: NavbarSection,
				parentKey: string | null | undefined,
			) => {
				const str = toBranchKeyStr(section, parentKey);
				if (!snapshots.has(str)) {
					const qk = navbarBranchQueryKeys.branch(section, parentKey);
					snapshots.set(str, {
						queryKey: qk,
						data: queryClient.getQueryData<NavbarBranchItem[]>(qk),
					});
				}
			};

			for (const item of items) {
				ensureSnapshot(fromSection, item.sourceParentKey);
				ensureSnapshot(toSection, item.parentKey);
			}

			const movedKeys = new Set(items.map((i) => i.itemKey));
			const movedItemData = new Map<string, NavbarBranchItem>();

			for (const { data } of snapshots.values()) {
				if (!data) {
					continue;
				}
				for (const entry of data) {
					if (movedKeys.has(entry.key) && !movedItemData.has(entry.key)) {
						movedItemData.set(entry.key, entry);
					}
				}
			}

			// Remove from source branches
			const sourceBranchStrs = [
				...new Set(
					items.map((i) => toBranchKeyStr(fromSection, i.sourceParentKey)),
				),
			];

			for (const str of sourceBranchStrs) {
				const snapshot = snapshots.get(str);
				if (!snapshot) {
					continue;
				}
				queryClient.setQueryData<NavbarBranchItem[]>(
					snapshot.queryKey,
					(old) => {
						if (!old) {
							return [];
						}
						return old.filter((item) => !movedKeys.has(item.key));
					},
				);
			}

			// Group items by target parent for insertion
			const itemsByTarget = new Map<string, MoveItemSpec[]>();
			for (const item of items) {
				const str = toBranchKeyStr(toSection, item.parentKey);
				let group = itemsByTarget.get(str);
				if (!group) {
					group = [];
					itemsByTarget.set(str, group);
				}
				group.push(item);
			}

			for (const [str, targetSpecs] of itemsByTarget) {
				const snapshot = snapshots.get(str);
				if (!snapshot) {
					continue;
				}
				const { queryKey } = snapshot;

				queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (old) => {
					const current = old ?? [];
					const filtered = current.filter((item) => !movedKeys.has(item.key));

					// Single-item DnD: splice at the precise targetIndex
					if (items.length === 1 && typeof items[0].targetIndex === "number") {
						const spec = items[0];
						const itemData = movedItemData.get(spec.itemKey);
						if (!itemData) {
							return filtered;
						}

						const entry: NavbarBranchItem = {
							...itemData,
							parentKey: spec.parentKey ?? null,
						};
						const result = [...filtered];

						let idx = spec.targetIndex ?? 0;
						if (spec.beforeKey != null) {
							const i = result.findIndex((x) => x.key === spec.beforeKey);
							idx = i === -1 ? result.length : i;
						} else if (spec.afterKey != null) {
							const i = result.findIndex((x) => x.key === spec.afterKey);
							idx = i === -1 ? result.length : i + 1;
						} else {
							const srcStr = toBranchKeyStr(fromSection, spec.sourceParentKey);
							if (srcStr === str) {
								const snap = snapshots.get(str)?.data;
								const srcIdx =
									snap?.findIndex((i) => i.key === spec.itemKey) ?? -1;
								if (srcIdx !== -1 && srcIdx < (spec.targetIndex ?? 0)) {
									idx -= 1;
								}
							}
						}

						result.splice(idx ?? filtered.length, 0, entry);
						return result;
					}

					// Batch: append in payload order
					const result = [...filtered];
					for (const spec of targetSpecs) {
						const itemData = movedItemData.get(spec.itemKey);
						if (itemData) {
							result.push({
								...itemData,
								parentKey: spec.parentKey ?? null,
							});
						}
					}
					return result;
				});
			}

			const seenBranchKeys = new Set<string>();
			for (const { queryKey } of snapshots.values()) {
				const dedupe = JSON.stringify(queryKey);
				if (seenBranchKeys.has(dedupe)) continue;
				seenBranchKeys.add(dedupe);
				const section = queryKey[1] as NavbarSection;
				syncFolderHasChildrenFromBranchQueryKey(queryClient, section, queryKey);
			}

			return { snapshots };
		},

		onError: (error, _input, context) => {
			if (context?.snapshots) {
				for (const { queryKey, data } of context.snapshots.values()) {
					queryClient.setQueryData(queryKey, data);
				}
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, _input, context) => {
			if (context?.snapshots) {
				for (const { queryKey } of context.snapshots.values()) {
					queryClient.invalidateQueries({ queryKey });
				}
			}
		},
	});
}
